import React, { useState, useEffect, useRef } from "react";
import { safeParseJson, titleCase } from "../utils";
import { SERVICE_TAG_STYLES, statusDisplayName } from "../constants";
import { SituationCard } from "../../kit";
import SubtaskSection from "./SubtaskSection";

// Compact due-date label for the row — "Jun 11" (no weekday/year). The full
// fmtDisplayDate in utils.js is used in the slide-out where there's room for it.
const fmtDueDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// ─── Summary cleaning ──────────────────────────────────────────────────────────
// fn_work_detail's AI `summary` embeds an analytics parenthetical mid-sentence,
// e.g. "The task was updated today (days_since_update=0) and has a recent
// comment…". Strip just the "(days_since_update=N)" fragment (and its leading
// space, so we don't leave a double space) and keep the rest of the note — the
// surrounding prose is meaningful and should read like a human-written line.
const cleanSummary = (raw) => {
  if (!raw) return "";
  let text = String(raw);
  text = text.replace(/\s*\(days_since_update[=:]\s*\d+\)/gi, "");
  text = text.replace(/\n{3,}/g, "\n\n");
  return text.trim();
};

// ─── Status pill colour (degrades to neutral grey for unknown statuses) ────────
// No canonical status enum is exposed to the client, so this maps the semantic
// values we do know and falls back to grey for everything else. Reuses the
// amber/red/emerald palette used by the signal chips below.
const statusPillClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "needs_assistance" || s === "blocked" || s === "stalled")
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
  if (s === "done" || s === "completed" || s === "closed" || s === "resolved")
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
  if (s.includes("overdue"))
    return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
  return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
};

// Priority pill colour — mirrors TaskHeader's palette so the row and the
// slide-out agree (urgent=red, high=orange, medium=yellow, else neutral).
// Degrades to grey for unknown values. item.priority may arrive JSON-encoded
// (ClickUp migration) — callers normalise it via safeParseJson first.
const priorityPillClass = (priority) => {
  const p = String(priority || "").toLowerCase();
  if (p === "urgent" || p === "p0" || p === "high")
    return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
  if (p === "medium" || p === "p1" || p === "normal")
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
  if (p === "low" || p === "p2")
    return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
  return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
};

const serviceTagClass = (service) => {
  if (!service) return SERVICE_TAG_STYLES[0];
  let h = 0;
  for (let i = 0; i < service.length; i++) h = (h * 31 + service.charCodeAt(i)) >>> 0;
  return SERVICE_TAG_STYLES[h % SERVICE_TAG_STYLES.length];
};

// ─── Done-row detection (work-page-fixes §A) ──────────────────────────────────
// Matches TaskListView's isDoneItem (same DONE_STATUSES) so a row partitioned
// under the "Completed" divider is also visually muted here. Done items only
// reach the list when "Include done" is on, so this never changes the default
// (toggle-off) view.
const DONE_STATUSES = new Set(["done", "canceled", "completed", "closed", "resolved"]);
const isDoneRow = (item) =>
  item?.bucket === "done" ||
  DONE_STATUSES.has(String(item?.status || "").toLowerCase());

// ─── Urgency signal chip ──────────────────────────────────────────────────────
// Renders a single urgency badge — the ONE signal that caused this card to sort
// to the top. stalled and needs_assistance are both amber but visually distinct:
// stalled is a filled chip (solid amber bg), needs_assistance is an outlined chip
// (amber border, transparent bg) so a glance tells them apart. broken_promises is
// red and short-circuits all others — no amber appears alongside it.
const UrgencySignalChip = ({ signal }) => {
  if (!signal) return null;
  const base = "flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium";
  switch (signal.type) {
    case "broken_promises":
      return (
        <span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400`}>
          <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </svg>
          {signal.count} broken
        </span>
      );
    case "stalled":
      return (
        <span className={`${base} bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400`}>
          <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
          Stalled
        </span>
      );
    case "needs_assistance":
      return (
        <span className={`${base} border border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400`}>
          <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <path d="M12 17h.01" />
          </svg>
          Needs help
        </span>
      );
    case "recent_messages":
      return (
        <span className={`${base} bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400`}>
          <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
          </svg>
          {signal.count}
        </span>
      );
    default:
      return null;
  }
};

// ─── Urgency dot ──────────────────────────────────────────────────────────────
// The urgency dot now lives inside <InboxRow> as a small vertical strip at the
// left edge (dot above the bulk-select checkbox), so <SituationCard> is invoked
// with tone="human" and no longer draws its own severity dot. Colour mirrors the
// urgencySignal cascade: red = broken_promises, amber = stalled or needs_assistance,
// gray = no urgency signal. The dot always renders (gray default) — see
// urgencyDotColor below.

// ─── InboxRow ───────────────────────────────────────────────────────────────────
// Compact inbox row with a bulk-selection checkbox + signal chip. Clicking the row
// opens the task slide-out (onSelect); the checkbox toggles bulk selection
// (onToggleSelect). Replaces the old CardShell + SituationCardPlaceholder pair.
// `bucket` is the active bucket key — when it's "waiting_on_them" every row shares
// the "waiting on client" status, so the status pill is suppressed to cut noise.
const InboxRow = ({ item, isSelected, isActive, onSelect, onToggleSelect, bucket, onAssign, onMove, onArchive, onEdit, onDelete, onAssigneeClick, onDueDateChange, onClientClick, expandedSubtasks, onToggleSubtasks, onSubtaskCountChange, revision }) => {
  const [openKebabId, setOpenKebabId] = useState(null);
  const [confirmArchiveId, setConfirmArchiveId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  // Close the kebab dropdown on any outside click or Escape. The dropdown's own
  // buttons call stopPropagation so their clicks don't reach this listener
  // before they've had a chance to act.
  useEffect(() => {
    if (!openKebabId) return;
    const handleClick = () => setOpenKebabId(null);
    const handleKeyDown = (e) => { if (e.key === "Escape") setOpenKebabId(null); };
    document.addEventListener("click", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openKebabId]);

  // Close the date picker on any outside click or Escape. The picker container
  // calls stopPropagation on click (and the toggle button does the same), so
  // their interactions don't reach this listener before they've acted.
  useEffect(() => {
    if (!showDatePicker) return;
    const handleClick = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    const handleKeyDown = (e) => { if (e.key === "Escape") setShowDatePicker(false); };
    document.addEventListener("click", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDatePicker]);

  // item.service / item.priority may arrive as JSON-encoded strings (ClickUp
  // migration) — normalise the same way TaskHeader does so the row never shows
  // raw JSON.
  const serviceRaw = safeParseJson(item.service);
  const serviceValue =
    serviceRaw && typeof serviceRaw === "object"
      ? serviceRaw.name || serviceRaw.service || ""
      : serviceRaw;
  const priorityRaw = safeParseJson(item.priority);
  const priorityValue =
    priorityRaw && typeof priorityRaw === "object"
      ? priorityRaw.priority || priorityRaw.name || ""
      : priorityRaw;

  // Line-2 metadata — composed the way a colleague would say it: client,
  // status, days in position, due/overdue, owner. See §3 of the work-row spec.
  const clientText = item.client || "Internal";
  const daysNum = Number(item.days_in_status) || 0;
  const isWaiting = bucket === "waiting_on_them";
  // Status-aware "X days …" label — the number is time-in-position from
  // days_in_status, NOT days until deadline. The suffix matches the actual
  // task status so a waiting-on-client task reads "Waiting on Client" rather
  // than the generic "Blocked" the old bucket-based label showed. Phrases use
  // the same title-case convention as the status pills/buckets (prepositions
  // like "on" stay lowercase, matching utils.js titleCase / TITLE_CASE_MINOR).
  // Unknown statuses fall back to "in {statusDisplayName}" so new statuses still
  // read OK. (isWaiting is still used below to bold the label in the Waiting on
  // Them bucket.)
  const daysStatusKey = String(item.status || "").toLowerCase();
  const daysStatusSuffix =
    daysStatusKey === "waiting_on_client" || daysStatusKey === "no_response"
      ? "Waiting on Client"
      : daysStatusKey === "needs_assistance"
        ? "Needs Assistance"
        : daysStatusKey === "stalled" || daysStatusKey === "blocked"
          ? "Blocked"
          : `in ${statusDisplayName(item.status) || "current status"}`;
  const daysLabel = `${daysNum} days ${daysStatusSuffix}`;
  const daysColor =
    daysNum > 90
      ? "text-red-600 dark:text-red-400"
      : daysNum > 30
        ? "text-amber-600 dark:text-amber-400"
        : "text-gray-500 dark:text-gray-400";
  // Due date — shown on its own line below the assignee as just the date with
  // year plus a warning icon when overdue/due-today (no relative day count). Both
  // sides are normalized to local midnight so the day diff is exact across month
  // and year boundaries (no month math involved).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueMs = item.due ? new Date(item.due + "T00:00:00").getTime() : NaN;
  const diffDays = Number.isNaN(dueMs)
    ? null
    : Math.round((dueMs - today.getTime()) / (1000 * 60 * 60 * 24));
  // "none" → no due date (section skipped). "overdue"/"today"/"future" drive
  // colour. The item.overdue flag is honoured alongside the day diff for robustness.
  const dueStatus = Number.isNaN(dueMs)
    ? "none"
    : diffDays < 0 || item.overdue
      ? "overdue"
      : diffDays === 0
        ? "today"
        : "future";
  const dueDateText = Number.isNaN(dueMs)
    ? ""
    : new Date(item.due + "T00:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
  // Colour class for the due date, reused inline on line 2.
  const dueColorClass =
    dueStatus === "overdue"
      ? "text-red-600 dark:text-red-400"
      : dueStatus === "today"
        ? "text-amber-600 dark:text-amber-400"
        : "text-gray-400 dark:text-gray-500";
  // ─── Urgency signal (one per card, highest priority wins) ───────────────────
  // The signal that caused this card to sort to the top — not every field that
  // happens to be true. Priority: broken_promises > stalled > needs_assistance >
  // recent_messages. broken_promises short-circuits all others (no amber on red).
  const brokenCount = Number(item.broken_promises) || 0;
  const isStalled = item.stalled === true;
  const statusLower = String(item.status || "").toLowerCase();
  const needsAssistance = statusLower === "needs_assistance";
  const recentMessages = Number(item.recent_messages) || 0;

  const urgencySignal =
    brokenCount > 0
      ? { type: "broken_promises", count: brokenCount }
      : isStalled
        ? { type: "stalled", reason: item.stalled_reason || "" }
        : needsAssistance
          ? { type: "needs_assistance" }
          : recentMessages > 0
            ? { type: "recent_messages", count: recentMessages }
            : null;

  // Urgency dot colour for the left-edge strip (sits above the checkbox). Mirrors
  // the urgencySignal cascade: red = broken_promises, amber = stalled or
  // needs_assistance, gray = no urgency signal. Always renders (gray default) so
  // every row has a dot at the left edge — <SituationCard> no longer draws its own
  // severity dot (we pass tone="human" below); this dot replaces it.
  const urgencyDotColor =
    brokenCount > 0
      ? "bg-red-500"
      : isStalled || needsAssistance
        ? "bg-amber-500"
        : "bg-gray-400";

  // Line 3 — when the stalled signal is active, show stalled_reason as a full
  // sentence (no truncation, wraps to be readable). Otherwise fall back to the
  // item summary, truncated to ~80 chars for compactness. broken_promises
  // short-circuits stalled so stalled_reason is NOT shown when broken_promises > 0.
  const isStalledSignal = urgencySignal?.type === "stalled";
  const summaryLine = isStalledSignal
    ? urgencySignal.reason
    : cleanSummary(item.summary || "");
  const summaryTrimmed = isStalledSignal
    ? summaryLine
    : summaryLine.length > 80
      ? summaryLine.slice(0, 77) + "..."
      : summaryLine;

  // ─── Subtask expansion (spec §4) ──────────────────────────────────────────
  // item.subtasks is the count from fn_work_list. When > 0 the row shows a
  // clickable "N steps" badge on Line 1; clicking toggles expansion. The
  // expanded list itself is rendered by <SubtaskSection> (below), which owns
  // its own fetch/add/rename/delete state and signals count changes back
  // through handleSubtaskCountChange → onSubtaskCountChange so the parent can
  // update the count in its cache.
  const subtaskCount = Number(item.subtasks) || 0;
  const isSubtaskExpanded = !!expandedSubtasks && expandedSubtasks.has(item.id);

  return (
    <>
    <SituationCard
      data-task-id={item.id}
      tone="human"
      bare
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item.id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSelect(item.id); }}
      className={`relative group min-h-[48px] border-b border-gray-200 dark:border-gray-700/50 px-4 py-2.5 transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 ${
        isActive ? "border-l-2 border-l-blue-500 bg-blue-50 dark:bg-blue-900/20" : ""
      } ${isDoneRow(item) ? "opacity-60" : ""}`}
    >
      {/* Main content area — three-column layout. Left edge: bulk-select checkbox
          only (a narrow vertical strip). Middle column: urgency dot + title +
          metadata. Right column: assignee + due date stacked right-aligned in an
          inner wrapper, with the kebab pulled out and vertically centered beside
          them. We pass tone="human" so <SituationCard> no longer draws its own
          severity dot — the dot now sits inline beside the title, and the
          click/keyboard handlers ride on the card via rest props (role/tabIndex/
          onClick/onKeyDown) since the tone branch spreads {...rest} onto the root. */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 min-w-0 flex-1">
      {/* Left-edge strip — bulk-select checkbox, vertically centered in the row.
          Hidden by default and revealed on row hover (group-hover) so the list
          stays uncluttered; stays visible once the row is selected so it can be
          unchecked. pointer-events-none while hidden so clicks pass through to
          the row (open slide-out) instead of toggling an invisible checkbox. */}
      <div className="flex items-center justify-center flex-shrink-0">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(item.id); }}
          className={`flex-shrink-0 transition-opacity duration-150 ${
            isSelected
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
          }`}
          aria-label={isSelected ? "Deselect task" : "Select task for bulk action"}
          aria-pressed={isSelected}
          title={isSelected ? "Deselect" : "Select for bulk action"}
        >
          {isSelected ? (
            // Checked — filled accent box (#32B1FF matches the Select-all toggle
            // in TaskListView) with a white checkmark inside.
            <svg className="w-3.5 h-3.5 text-[#32B1FF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5.5" stroke="white" />
            </svg>
          ) : (
            // Unchecked — muted gray outline, transparent background (no white fill).
            <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="3" />
            </svg>
          )}
        </button>
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        {/* Line 1: title (bold, truncate) + urgency signal chip */}
        <div className="flex items-center gap-2 min-w-0 flex-wrap md:flex-nowrap">
          <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 mr-1 ${urgencyDotColor}`} aria-hidden="true" />
          <span className="min-w-0 flex-1 md:truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {item.title || "Untitled"}
          </span>
          {/* Recurring badge (spec §7) — small loop icon when item.recurring === true.
              Informational only: muted gray so it does not compete with urgency
              signals (broken_promises red, stalled amber). Sits directly beside the
              title so a recurring task ("Monthly performance report") is immediately
              recognisable as one that comes back. */}
          {item.recurring === true && (
            <span
              className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
              title="This task recurs — it will come back"
            >
              <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              Recurring
            </span>
          )}
          {/* The Stalled pill lives beside the assignee in the right column (see
              below), so suppress it here on Line 1 when the active signal is
              Stalled to avoid showing it twice. Other signals (broken_promises,
              needs_assistance, recent_messages) still render here. */}
          {!isStalledSignal && <UrgencySignalChip signal={urgencySignal} />}
          {/* Subtask count (spec §4) — clickable badge when item.subtasks > 0.
              stopPropagation so the click expands subtasks instead of opening
              the slide-out. Mirrors SubtaskList.js styling (chevron + count). */}
          {subtaskCount > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleSubtasks && onToggleSubtasks(item.id); }}
              className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title={`${subtaskCount} subtask${subtaskCount === 1 ? "" : "s"} — click to ${isSubtaskExpanded ? "collapse" : "expand"}`}
            >
              <svg
                className={`w-3 h-3 transition-transform ${isSubtaskExpanded ? "rotate-90" : ""}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
              {subtaskCount} step{subtaskCount === 1 ? "" : "s"}
            </button>
          )}
        </div>
        {/* Line 2: client · "X days in {status}" (muted, smaller) */}
        <div className="flex items-center gap-1.5 min-w-0 text-xs">
          {item.client_id ? (
            <span
              className="flex-shrink-0 min-w-0 truncate max-w-[10rem] text-gray-400 dark:text-gray-500"
            >
              {clientText}
            </span>
          ) : (
            <span className="flex-shrink-0 min-w-0 truncate max-w-[10rem] italic text-gray-400 dark:text-gray-500">
              Internal
            </span>
          )}
          {daysNum > 0 && (
            <>
              <span className="flex-shrink-0 text-gray-300 dark:text-gray-600">·</span>
              <span className={`flex-shrink-0 ${daysColor} ${isWaiting ? "font-semibold" : ""}`}>
                {daysLabel}
              </span>
            </>
          )}
        </div>
        {/* Line 3 (optional): stalled reason or summary. When the stalled signal
            is active, the stalled_reason renders as a full sentence (wraps, no
            truncation) in amber to tie it to the Stalled chip. Otherwise the item
            summary renders muted italic, truncated ~80 chars with full text on hover.
            broken_promises short-circuits stalled — the regular summary shows instead. */}
        {summaryTrimmed && (
          <div
            className={`text-xs break-words line-clamp-2 md:line-clamp-none ${
              isStalledSignal
                ? "text-amber-600 dark:text-amber-400"
                : "italic text-gray-400 dark:text-gray-500 md:truncate"
            }`}
            title={isStalledSignal ? undefined : summaryLine}
          >
            {summaryTrimmed}
          </div>
        )}
      </div>
      {/* Right column — assignee + due date stacked right-aligned in an inner
          wrapper, with the kebab pulled out as a sibling so it stays vertically
          centered relative to both rows. */}
      <div className="flex-shrink-0 flex flex-col md:flex-row items-start md:items-center justify-start md:justify-end gap-2 w-full md:w-auto">
        {/* Assignee (top) + due date (bottom) stacked, right-aligned. Tight gap
            keeps the due date aligned directly beneath the assignee. */}
        <div className="flex flex-col items-start md:items-end gap-1 w-full md:w-auto">
          {/* Top row: Stalled pill (when the active signal is Stalled) + assignee
              (clickable), side by side. The Stalled pill always lives here beside
              the assignee regardless of whether a due date exists. */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-1 w-full md:w-auto">
            {isStalledSignal && <UrgencySignalChip signal={urgencySignal} />}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAssigneeClick && onAssigneeClick(item); }}
              className="flex-shrink-0 max-w-full md:max-w-[8rem] truncate text-left md:text-right rounded px-1 -mx-1 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors cursor-pointer text-sm"
            >
              {item.owner ? (
                <>
                  {/* Person icon (§D, mobile only) — sits before the owner name on its
                      own line in the stacked mobile row. md:hidden keeps the desktop
                      assignee unchanged. The button already carries `truncate` +
                      `max-w-full`, so a long name ellipsises on one line (no wrap). */}
                  <svg className="w-3 h-3 inline-block align-text-bottom mr-1 md:hidden text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span className="text-gray-500 dark:text-gray-400">{item.owner}</span>
                </>
              ) : (
                <span className="font-semibold text-amber-600 dark:text-amber-400">Unassigned</span>
              )}
            </button>
          </div>
          {/* Due date row — sits directly under the assignee, same right edge
              (or "+ Add due date" when null). Same text-sm size as the assignee
              above it. Clickable button, bg-fill hover (matches task slide-out). Red + AlertTriangle
              when overdue. Toggles a small date-picker popover (input type
              "date") below it. */}
          <div className="relative flex items-center gap-1">
            {dueDateText ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowDatePicker((v) => !v); }}
                className={`flex-shrink-0 inline-flex items-center gap-1 whitespace-nowrap rounded px-1 -mx-1 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors cursor-pointer text-sm ${dueColorClass}`}
              >
                {/* Warning icon (§D): alert triangle for overdue on all breakpoints
                    (desktop overdue unchanged). For "today" the alert triangle is
                    desktop-only (`hidden md:block`) so the desktop today row stays as
                    it was, while mobile shows the calendar icon below. Future dates
                    get no warning. */}
                {(dueStatus === "overdue" || dueStatus === "today") && (
                  <svg className={`w-3 h-3 flex-shrink-0 ${dueStatus === "today" ? "hidden md:block" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                )}
                {/* Calendar icon (§D, mobile only) — non-overdue dates (today +
                    future) get a calendar affordance on mobile so the due date line is
                    clearly a date. md:hidden keeps the desktop due date unchanged. */}
                {(dueStatus === "today" || dueStatus === "future") && (
                  <svg className="w-3 h-3 flex-shrink-0 md:hidden" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                )}
                {dueDateText}
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowDatePicker((v) => !v); }}
                className="flex-shrink-0 inline-flex items-center gap-1 whitespace-nowrap rounded px-1 -mx-1 text-sm text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
              >
                + Add due date
              </button>
            )}
            {showDatePicker && (
              <div
                ref={datePickerRef}
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-2"
              >
                <input
                  type="date"
                  defaultValue={item.due || ""}
                  autoFocus
                  onChange={(e) => {
                    e.stopPropagation();
                    const val = e.target.value || "";
                    onDueDateChange && onDueDateChange(item, val);
                    setShowDatePicker(false);
                  }}
                  onKeyDown={(e) => { if (e.key === "Escape") setShowDatePicker(false); }}
                  className="text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </div>
            )}
          </div>
        </div>
        {/* Kebab — pulled out of the assignee row and vertically centered
            relative to both the assignee and due date rows. flex-shrink-0 so it
            is never squeezed. stopPropagation keeps the click from opening the
            slide-out. The row container is `relative`, so the dropdown below
            positions off it (right-0 top-full mt-1 z-50). */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpenKebabId(openKebabId === item.id ? null : item.id);
          }}
          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors absolute top-2.5 right-2.5 md:static"
          aria-label="Row actions"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>
        </button>
      </div>
      </div>
      {openKebabId === item.id && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-lg shadow-xl py-1 min-w-[160px]">
          <button
            onClick={(e) => { e.stopPropagation(); onMove(item, e.currentTarget); setOpenKebabId(null); }}
            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="4" y1="7" x2="20" y2="7"/><polyline points="16 3 20 7 16 11"/><line x1="20" y1="17" x2="4" y2="17"/><polyline points="8 13 4 17 8 21"/></svg> Status
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirmArchiveId === item.id) {
                onArchive(item);
                setConfirmArchiveId(null);
                setOpenKebabId(null);
              } else {
                setConfirmArchiveId(item.id);
                setTimeout(() => setConfirmArchiveId(null), 3000);
              }
            }}
            className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${confirmArchiveId === item.id ? "text-yellow-400" : "text-green-600 dark:text-green-400"} hover:bg-gray-100 dark:hover:bg-white/10`}
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            {confirmArchiveId === item.id ? "Click again to archive" : "Done"}
          </button>
          <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirmDeleteId === item.id) {
                onDelete(item, '');
                setConfirmDeleteId(null);
                setOpenKebabId(null);
              } else {
                setConfirmDeleteId(item.id);
                setTimeout(() => setConfirmDeleteId(null), 3000);
              }
            }}
            className="w-full text-left px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-white/10 flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
            {confirmDeleteId === item.id ? "Click again to delete" : "Delete"}
          </button>
        </div>
      )}
    </SituationCard>
    {/* Subtask rows (spec §4) — rendered as indented siblings beneath the
        parent card when expanded. <SubtaskSection> owns its own fetch /
        add / rename / delete state and signals count changes back through
        onCountChange so the parent can refresh the badge count. */}
    {isSubtaskExpanded && (
      <div className="ml-8 mt-1 border-l-2 border-gray-200 dark:border-gray-700 pl-3 pb-1">
        <SubtaskSection revision={revision} parentId={item.id} onCountChange={(newCount) => onSubtaskCountChange?.(item.id, newCount)} compact />
      </div>
    )}
    </>
  );
};

export default InboxRow;
